import styled from 'styled-components';
import ExperiencesSearchForm from './ExperiencesSearchForm';
import {ChangeEvent, useCallback, useState} from 'react';
import {getHrExperiences} from '../../utils/api';
import useSWRInfinite from 'swr/infinite';
import ExperiencesTable from './ExperiencesTable';
import {useTranslation} from 'react-i18next';

const PAGE_SIZE = 30;

const InitialValues: HRExperiencesFormData = {
  fullName: '',
  playing: true,
  'on-boarding': true,
  completed: true,
};

type ExperiencesProps = {
  token?: string;
  jwtToken?: string;
  isSharedOne?: boolean;
};

const Experiences = ({jwtToken, token, isSharedOne}: ExperiencesProps) => {
  //order experiences
  const [orderBy, setOrderBy] = useState<'lastName' | 'email' | 'lastAccess'>(
    'lastAccess'
  );
  const [ordering, setOrdering] = useState<'asc' | 'desc'>('asc');

  //search experiences
  const [formData, setFormData] =
    useState<HRExperiencesFormData>(InitialValues);

  const {t} = useTranslation();

  const getKey = (pageIndex: number, previousPageData: []) => {
    if (previousPageData && !previousPageData.length) {
      return null;
    }
    return `?page=${pageIndex + 1}&num=${PAGE_SIZE}&fullname=${
      formData.fullName
    }&playing=${formData['playing']}&on-boarding=${
      formData['on-boarding']
    }&completed=${
      formData.completed
    }&orderBy=${orderBy}&ordering=${ordering}&token=${token ? token : ''}`;
  };

  const {data, size, setSize, error, isValidating} = useSWRInfinite(
    getKey,
    (baseQuery) => getHrExperiences(jwtToken || '', baseQuery),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      revalidateFirstPage: false,
      persistSize: true,
    }
  );

  const experiences = data ? ([] as HRExperience[]).concat(...data) : [];
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.length < PAGE_SIZE);

  const handleSearchSubmit = useCallback(
    (t: string) => {
      setFormData({
        ...formData,
        fullName: t,
      });
    },
    [formData]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const target = e.target;
      const name = target.name;
      const value = target.type === 'checkbox' ? target.checked : target.value;

      // @ts-ignore
      setFormData({
        ...formData,
        [name]: value,
      });
    },
    [formData]
  );

  const handleSearchReset = useCallback(() => {
    setFormData({
      ...formData,
      fullName: '',
    });
  }, [formData]);
  return (
    <AllExperiences>
      <SectionTitle>{t('dashboard.all-plays')}</SectionTitle>
      <ExperiencesSearchForm
        formData={formData}
        onSubmit={handleSearchSubmit}
        onChange={handleInputChange}
      />
      {formData.fullName && (
        <Searching>
          Filtro aggiunto: <span>{formData.fullName}</span>
          <button onClick={handleSearchReset}>
            <u>rimuovi</u>
          </button>
        </Searching>
      )}
      <ExperiencesTable
        isSharedOne={isSharedOne}
        experiences={experiences}
        loading={isValidating}
        error={error}
        isReachingEnd={isReachingEnd}
        isEmpty={isEmpty}
        loadMore={() => setSize(size + 1)}
        orderBy={orderBy}
        ordering={ordering}
        onOrderByChange={setOrderBy}
        onOrderingChange={() =>
          setOrdering(ordering === 'asc' ? 'desc' : 'asc')
        }
      />
    </AllExperiences>
  );
};

export default Experiences;

const AllExperiences = styled.section`
  max-width: ${(p) => p.theme.sizes.mwInner};
  margin: 70px auto;
  padding: 0 20px;
`;

const Searching = styled.p`
  margin-top: 1rem;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  column-gap: 12px;

  span {
    display: block;
    padding: 3px 6px;
    background-color: ${(p) => p.theme.colors.violet300};
    color: ${(p) => p.theme.colors.white};
    font-size: 14px;
    font-weight: 400;
    line-height: 1;
    border-radius: 16px;
  }

  button {
    all: unset;
    display: block;
    cursor: pointer;
  }
`;

const SectionTitle = styled.h3`
  margin-bottom: 24px;
  font-weight: 700;
  font-size: 28px;
  font-family: inherit;
`;
